/**
 * Service for managing API endpoint overrides in development environments
 * Allows developers to point their local environment to staging or production APIs
 */
class ApiConfigService {
    private readonly STORAGE_KEY = 'apiEndpointOverride'
    private readonly KNOWN_STAGES = ['debug', 'dev', 'dev-ai', 'staging', 'prod']

    /**
     * Get the current API override setting
     */
    getApiOverride(): 'default' | 'staging' | 'prod' {
        const stored = localStorage.getItem(this.STORAGE_KEY)
        if (stored === 'staging' || stored === 'prod') {
            return stored
        }
        return 'default'
    }

    /**
     * Set the API override and log out the user
     * Auth tokens are environment-specific, so we must clear them when switching
     */
    setApiOverride(endpoint: 'default' | 'staging' | 'prod'): void {
        if (endpoint === 'default') {
            localStorage.removeItem(this.STORAGE_KEY)
        } else {
            localStorage.setItem(this.STORAGE_KEY, endpoint)
        }

        // Show brief notification before logout
        console.log(
            `Switching API environment to: ${endpoint === 'default' ? 'default environment' : endpoint}. Logging out...`
        )

        // Clear auth tokens and redirect to login
        // This is necessary because tokens are tied to specific environments
        setTimeout(() => {
            // Clear all auth-related localStorage items
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')

            // Redirect to login page where user can authenticate with the new environment
            window.location.href = '/login'
        }, 500)
    }

    /**
     * Get the effective stage to use (considering override)
     */
    getEffectiveStage(): string {
        const override = this.getApiOverride()

        if (override === 'default') {
            // Use environment variable
            return import.meta.env.VITE_APP_API_STAGE || 'prod'
        }

        return override
    }

    /**
     * Get the effective domain to use (considering override)
     */
    getEffectiveDomain(): string {
        const override = this.getApiOverride()

        if (override === 'default') {
            // Use environment variable
            return import.meta.env.VITE_APP_API_DOMAIN || 'moddy.ai'
        }

        // For staging and prod, always use moddy.ai
        return 'moddy.ai'
    }

    /**
     * Check if we should use the override
     */
    shouldUseOverride(): boolean {
        return this.getApiOverride() !== 'default'
    }

    /**
     * Check if the current environment allows API override
     */
    isOverrideAllowed(): boolean {
        const currentStage = import.meta.env.VITE_APP_API_STAGE || 'prod'
        // Only allow override in development environments
        return !['staging', 'prod'].includes(currentStage)
    }

    /**
     * Get display text for current environment
     */
    getCurrentEnvironmentDisplay(): string {
        const stage = import.meta.env.VITE_APP_API_STAGE || 'prod'
        const domain = import.meta.env.VITE_APP_API_DOMAIN || 'moddy.ai'

        if (this.KNOWN_STAGES.includes(stage)) {
            return `${stage}.${domain}`
        }
        // Developer stage
        return `${stage}.${domain}`
    }
}

// Export singleton instance
export const apiConfigService = new ApiConfigService()
